import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Badge, Table, Spinner,
  Button, Collapse, Alert, Nav
} from 'react-bootstrap';

const API_BASE = '';

function staleness(lastUpdate) {
  if (!lastUpdate) return 'unknown';
  const diffMs = Date.now() - new Date(lastUpdate).getTime();
  const hours = diffMs / 3600000;
  if (hours < 24) return 'fresh';
  if (hours < 72) return 'stale';
  return 'old';
}

function StaleBadge({ lastUpdate }) {
  const s = staleness(lastUpdate);
  const map = { fresh: 'success', stale: 'warning', unknown: 'secondary', old: 'danger' };
  const label = { fresh: 'Fresh', stale: 'Stale (>1d)', unknown: 'No data', old: 'Old (>3d)' };
  return <Badge bg={map[s]}>{label[s]}</Badge>;
}

function HealthBadge({ errorCount, storeCount }) {
  if (storeCount === 0) return <Badge bg="secondary">No stores</Badge>;
  if (errorCount === 0) return <Badge bg="success">Healthy</Badge>;
  if (errorCount <= 2) return <Badge bg="warning" text="dark">Degraded</Badge>;
  return <Badge bg="danger">Errors ({errorCount})</Badge>;
}

function formatDate(dt) {
  if (!dt) return '—';
  return dt.replace('T', ' ').slice(0, 16);
}

function ErrorCell({ error }) {
  const [show, setShow] = useState(false);
  if (!error) return null;
  return (
    <div className="mt-1">
      <Badge
        bg="danger"
        style={{ cursor: 'pointer', fontWeight: 'normal', maxWidth: '100%' }}
        onClick={() => setShow(v => !v)}
        title={show ? 'Click to collapse' : 'Click to expand'}
      >
        ⚠ {show ? '▲' : '▼'} last error
      </Badge>
      {show && (
        <div
          className="mt-1 p-1 rounded"
          style={{
            background: '#fff3f3',
            border: '1px solid #f5c6cb',
            fontSize: '0.72rem',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxWidth: 420,
          }}
        >
          {error.when && <span className="text-muted">{error.when} — </span>}
          {error.message}
        </div>
      )}
    </div>
  );
}

function SortHeader({ field, current, dir, onSort, children }) {
  const active = current === field;
  return (
    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => onSort(field)}>
      {children}{' '}
      <span style={{ opacity: active ? 1 : 0.25, fontSize: '0.75em' }}>
        {active ? (dir === 'asc' ? '▲' : '▼') : '⇅'}
      </span>
    </th>
  );
}

function sortRows(rows, field, dir) {
  return [...rows].sort((a, b) => {
    let va = a[field], vb = b[field];
    if (va == null && vb == null) return 0;
    if (va == null) return dir === 'asc' ? 1 : -1;
    if (vb == null) return dir === 'asc' ? -1 : 1;
    if (typeof va === 'string') return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    return dir === 'asc' ? (va > vb ? 1 : va < vb ? -1 : 0) : (va < vb ? 1 : va > vb ? -1 : 0);
  });
}

function ChainRow({ chain, onExpand, expanded }) {
  const sources = React.useContext(ChainSourcesContext);
  const canExpand = chain.storeCount > 0 || chain.errorCount > 0 || chain.chainLastError;
  const triedDate = chain.lastAttempt || chain.lastUpdate;
  const showSuccessDate = chain.lastUpdate && chain.lastAttempt && chain.lastUpdate !== chain.lastAttempt;
  return (
    <tr>
      <td>
        <strong>{chain.chainId}</strong>
        <SourceBadge source={sources[chain.chainId]} />
        {chain.chainLastError && <ErrorCell error={chain.chainLastError} />}
      </td>
      <td>{chain.storeCount > 0 ? chain.storeCount : <span className="text-muted">0</span>}</td>
      <td>{chain.totalRuns}</td>
      <td>
        <span
          style={canExpand ? { cursor: 'pointer' } : undefined}
          title={canExpand ? 'Click to see details' : undefined}
          onClick={canExpand ? () => onExpand(chain.chainId) : undefined}
        >
          <HealthBadge errorCount={chain.errorCount} storeCount={chain.storeCount} />
        </span>
      </td>
      <td>
        <small className="text-muted">{formatDate(triedDate)}</small>
        {showSuccessDate && (
          <div><small style={{ fontSize: '0.7em', color: '#888' }}>updated: {formatDate(chain.lastUpdate)}</small></div>
        )}
      </td>
      <td>
        {canExpand && (
          <Button size="sm" variant="outline-primary" onClick={() => onExpand(chain.chainId)}>
            {expanded ? 'Hide' : 'Details'}
          </Button>
        )}
      </td>
    </tr>
  );
}

function ChainDetail({ chainId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRuns, setShowRuns] = useState(false);
  const [showOnlyFailed, setShowOnlyFailed] = useState(false);
  const [storeSort, setStoreSort] = useState({ field: 'lastUpdated', dir: 'desc' });

  const handleStoreSort = (field) => {
    setStoreSort(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/api/admin/chain/${chainId}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [chainId]);

  if (loading) return <tr><td colSpan={6}><Spinner size="sm" /> Loading stores…</td></tr>;
  if (error) return <tr><td colSpan={6}><Alert variant="danger" className="mb-0 py-1">{error}</Alert></td></tr>;
  if (!data) return null;

  return (
    <tr>
      <td colSpan={6} className="p-0">
        <div className="p-3 bg-light border-bottom">
          <Row className="mb-2">
            <Col xs="auto">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setShowRuns(v => !v)}
              >
                {showRuns ? 'Hide' : 'Show'} run history ({data.runs.length})
              </Button>
            </Col>
          </Row>

          {data.chainLastError && (
            <Alert variant="danger" className="py-2 mb-2" style={{ fontSize: '0.82rem' }}>
              <strong>Chain-level error</strong>
              {data.chainLastError.when && <span className="text-muted ms-2">{data.chainLastError.when}</span>}
              <div style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 4 }}>
                {data.chainLastError.message}
              </div>
            </Alert>
          )}

          {data.storesXml && (
            <div className="mb-2">
              <small className="text-muted me-1">Stores XML:</small>
              <a href={`${data.storesXml}`} target="_blank" rel="noreferrer"
                style={{ fontSize: '0.8rem' }}>
                {data.storesXml.split('/').pop()}
              </a>
            </div>
          )}

          {data.stores.length > 0 && (
            <>
              <div className="mb-2 d-flex align-items-center gap-2">
                <strong>Stores with price data ({data.storeCount})</strong>
                {data.stores.some(s => s.lastError) && (
                  <Button
                    size="sm"
                    variant={showOnlyFailed ? 'danger' : 'outline-danger'}
                    onClick={() => setShowOnlyFailed(v => !v)}
                  >
                    {showOnlyFailed
                      ? `Showing ${data.stores.filter(s => s.lastError).length} failed`
                      : `Show only failed (${data.stores.filter(s => s.lastError).length})`}
                  </Button>
                )}
              </div>
            <Table size="sm" bordered className="mb-2 bg-white">
              <thead>
                <tr>
                  <SortHeader field="storeId" current={storeSort.field} dir={storeSort.dir} onSort={handleStoreSort}>Store ID</SortHeader>
                  <SortHeader field="productCount" current={storeSort.field} dir={storeSort.dir} onSort={handleStoreSort}>Products</SortHeader>
                  <SortHeader field="fileSizeKB" current={storeSort.field} dir={storeSort.dir} onSort={handleStoreSort}>Size</SortHeader>
                  <SortHeader field="lastAttempt" current={storeSort.field} dir={storeSort.dir} onSort={handleStoreSort}>Last tried</SortHeader>
                  <SortHeader field="lastUpdated" current={storeSort.field} dir={storeSort.dir} onSort={handleStoreSort}>Last updated</SortHeader>
                  <th>Freshness</th>
                  <SortHeader field="distanceKm" current={storeSort.field} dir={storeSort.dir} onSort={handleStoreSort}>Distance</SortHeader>
                  <th>Raw XML</th>
                  <th>Last Error</th>
                </tr>
              </thead>
              <tbody>
                {sortRows(
                  showOnlyFailed ? data.stores.filter(s => s.lastError) : data.stores,
                  storeSort.field, storeSort.dir
                ).map(s => (
                  <tr key={s.storeId} className={s.lastError ? 'table-warning' : ''}>
                    <td><code>{s.storeId}</code></td>
                    <td>{s.productCount !== null ? s.productCount.toLocaleString() : '—'}</td>
                    <td>{s.fileSizeKB} KB</td>
                    <td>{formatDate(s.lastAttempt)}</td>
                    <td>{formatDate(s.lastUpdated)}</td>
                    <td><StaleBadge lastUpdate={s.lastUpdated} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {s.distanceKm !== null
                        ? <span>{s.distanceKm.toFixed(1)} km</span>
                        : <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>missing</Badge>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {s.xmlFiles ? (
                        <span style={{ fontSize: '0.75rem' }}>
                          {[
                            { key: 'price',     label: 'Price' },
                            { key: 'priceFull', label: 'Full' },
                            { key: 'promo',     label: 'Promo' },
                            { key: 'promoFull', label: 'PromoFull' },
                          ].map(({ key, label }) =>
                            s.xmlFiles[key] ? (
                              <a
                                key={key}
                                href={`${s.xmlFiles[key]}`}
                                target="_blank"
                                rel="noreferrer"
                                className="me-1"
                              >
                                {label}
                              </a>
                            ) : null
                          )}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td style={{ maxWidth: 260 }}>
                      {s.lastError
                        ? <ErrorCell error={s.lastError} />
                        : <span className="text-muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </>
          )}

          <Collapse in={showRuns}>
            <div>
              <strong>Run history</strong>
              <Table size="sm" bordered className="mt-1 bg-white">
                <thead>
                  <tr>
                    <th>Started at</th>
                    <th>Store</th>
                    <th>Files downloaded</th>
                    <th>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {data.runs.slice(0, 30).map(r => (
                    <tr key={r.runKey} className={r.errors > 0 ? 'table-warning' : ''}>
                      <td>{formatDate(r.startedAt)}</td>
                      <td>{r.storeId ?? '—'}</td>
                      <td>{r.filesDownloaded}</td>
                      <td>
                        {r.errors > 0
                          ? <Badge bg="danger">{r.errors}</Badge>
                          : <span className="text-muted">0</span>}
                      </td>
                    </tr>
                  ))}
                  {data.runs.length > 30 && (
                    <tr><td colSpan={4} className="text-muted text-center">… {data.runs.length - 30} more runs</td></tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Collapse>
        </div>
      </td>
    </tr>
  );
}

function SourceBadge({ source }) {
  if (!source) return null;
  if (source.type === 'web') {
    return (
      <a href={source.url} target="_blank" rel="noreferrer"
        style={{ fontSize: '0.7rem', marginLeft: 6, textDecoration: 'none' }}
        title={source.url}
      >
        <Badge bg="info" style={{ fontWeight: 'normal' }}>🔗 source</Badge>
      </a>
    );
  }
  if (source.type === 'ftp') {
    const ftpUrl = `ftp://${source.user}${source.pass ? ':' + source.pass : ''}@${source.host}/`;
    const label = source.pass ? `${source.user} / ${source.pass}` : source.user;
    return (
      <a href={ftpUrl} target="_blank" rel="noreferrer"
        style={{ fontSize: '0.7rem', marginLeft: 6, textDecoration: 'none' }}
        title={`FTP: ${ftpUrl}`}
      >
        <Badge bg="secondary" style={{ fontWeight: 'normal' }}>🔗 ftp: {label}</Badge>
      </a>
    );
  }
  return null;
}

// Shared context for chain sources so we only fetch once
const ChainSourcesContext = React.createContext({});

function formatRunId(runId) {
  // "20260307_213540" → "2026-03-07 21:35:40"
  const m = runId.match(/^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})$/);
  if (!m) return runId;
  return `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}:${m[6]}`;
}

function RunDetail({ runId }) {
  const sources = React.useContext(ChainSourcesContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedChain, setExpandedChain] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/admin/runs/${runId}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [runId]);

  if (loading) return <tr><td colSpan={5}><Spinner size="sm" /> Loading…</td></tr>;
  if (error) return <tr><td colSpan={5}><Alert variant="danger" className="mb-0 py-1">{error}</Alert></td></tr>;
  if (!data) return null;

  const chains = Object.entries(data.summary || {}).sort((a, b) => b[1].failed - a[1].failed);

  return (
    <tr>
      <td colSpan={5} className="p-0">
        <div className="p-3 bg-light border-bottom">
          <Table size="sm" bordered className="mb-0 bg-white">
            <thead className="table-light">
              <tr>
                <th>Chain</th>
                <th>Total</th>
                <th>Success</th>
                <th>Failed</th>
                <th>No Promos</th>
                <th>Promos Applied</th>
              </tr>
            </thead>
            <tbody>
              {chains.map(([chain, s]) => (
                <React.Fragment key={chain}>
                  <tr
                    className={s.failed > 0 ? 'table-warning' : ''}
                    style={s.failed > 0 ? { cursor: 'pointer' } : undefined}
                    onClick={s.failed > 0 ? () => setExpandedChain(p => p === chain ? null : chain) : undefined}
                  >
                    <td>
                      <strong>{chain}</strong>
                      <SourceBadge source={sources[chain]} />
                      {s.failed > 0 && (
                        <Badge bg="danger" className="ms-2" style={{ fontSize: '0.7rem' }}>
                          {expandedChain === chain ? '▲' : '▼'} {s.failed} failed
                        </Badge>
                      )}
                    </td>
                    <td>{s.total_stores}</td>
                    <td>{s.successful}</td>
                    <td>{s.failed > 0 ? <Badge bg="danger">{s.failed}</Badge> : <span className="text-muted">0</span>}</td>
                    <td>{s.missing_promotions ?? '—'}</td>
                    <td>{s.promotions_applied_total?.toLocaleString() ?? '—'}</td>
                  </tr>
                  {expandedChain === chain && s.failed > 0 && (
                    <tr>
                      <td colSpan={6} className="p-0">
                        <div className="p-2" style={{ background: '#fff3f3' }}>
                          <Table size="sm" className="mb-0" style={{ fontSize: '0.78rem' }}>
                            <thead>
                              <tr><th>Store ID</th><th>Error</th></tr>
                            </thead>
                            <tbody>
                              {(data.summary[chain].failed_stores || []).map(fs => (
                                <tr key={fs.store_id}>
                                  <td><code>{fs.store_id}</code></td>
                                  <td style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{fs.error}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </div>
      </td>
    </tr>
  );
}

function RunHistory() {
  const [runs, setRuns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRun, setExpandedRun] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/admin/runs`)
      .then(r => r.json())
      .then(d => { setRuns(d.runs); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  if (loading) return <div className="p-4 text-center"><Spinner /></div>;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!runs || runs.length === 0) return <Alert variant="info">No update runs found.</Alert>;

  return (
    <Card>
      <Card.Header><strong>Update Run History</strong> <Badge bg="secondary">{runs.length}</Badge></Card.Header>
      <Card.Body className="p-0">
        <Table hover responsive className="mb-0">
          <thead className="table-light">
            <tr>
              <th>Timestamp</th>
              <th>Mode</th>
              <th>Chains</th>
              <th>Progress</th>
              <th>Result</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {runs.map(run => {
              const totalFailed = Object.values(run.summary).reduce((s, c) => s + (c.failed || 0), 0);
              const totalSuccess = Object.values(run.summary).reduce((s, c) => s + (c.successful || 0), 0);
              const pct = run.progress?.percentage ?? null;
              const isExpanded = expandedRun === run.runId;
              return (
                <React.Fragment key={run.runId}>
                  <tr className={totalFailed > 0 ? 'table-warning' : ''}>
                    <td><small>{formatRunId(run.runId)}</small></td>
                    <td>
                      <Badge bg="secondary">{run.mode}</Badge>
                      {run.maxDistance && <small className="ms-1 text-muted">{run.maxDistance}km</small>}
                    </td>
                    <td>{run.chainCount}</td>
                    <td>
                      {pct !== null
                        ? <span>{run.progress.completed}/{run.progress.total} <small className="text-muted">({pct}%)</small></span>
                        : '—'}
                    </td>
                    <td>
                      <span className="text-success me-2">✓ {totalSuccess}</span>
                      {totalFailed > 0 && <span className="text-danger">✗ {totalFailed}</span>}
                    </td>
                    <td>
                      <Button size="sm" variant="outline-primary" onClick={() => setExpandedRun(p => p === run.runId ? null : run.runId)}>
                        {isExpanded ? 'Hide' : 'Details'}
                      </Button>
                    </td>
                  </tr>
                  {isExpanded && <RunDetail runId={run.runId} />}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}

export default function Backoffice() {
  const [activeTab, setActiveTab] = useState('chains');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedChain, setExpandedChain] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [chainSort, setChainSort] = useState({ field: 'chainId', dir: 'asc' });
  const [chainSources, setChainSources] = useState({});

  const handleChainSort = (field) => {
    setChainSort(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/api/admin/status`)
      .then(r => r.json())
      .then(d => {
        setStatus(d.chains);
        setGeneratedAt(d.generatedAt);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(`${API_BASE}/api/admin/chain-sources`)
      .then(r => r.json())
      .then(d => setChainSources(d))
      .catch(() => {});
  }, []);

  const handleExpand = (chainId) => {
    setExpandedChain(prev => prev === chainId ? null : chainId);
  };

  // Summary stats
  const totalStores = status ? status.reduce((s, c) => s + c.storeCount, 0) : 0;
  const totalErrors = status ? status.reduce((s, c) => s + c.errorCount, 0) : 0;
  const totalRuns   = status ? status.reduce((s, c) => s + c.totalRuns, 0) : 0;
  const freshChains = status ? status.filter(c => staleness(c.lastUpdate) === 'fresh').length : 0;
  const chainsWithData = status ? status.filter(c => c.storeCount > 0).length : 0;

  return (
    <ChainSourcesContext.Provider value={chainSources}>
    <Container className="mt-4 mb-5">
      <Row className="mb-3 align-items-center">
        <Col>
          <h2 className="mb-0">Backoffice</h2>
          {generatedAt && (
            <small className="text-muted">
              Generated at {new Date(generatedAt).toLocaleTimeString()}
            </small>
          )}
        </Col>
        <Col xs="auto">
          <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>
            {loading ? <Spinner size="sm" /> : '↻ Refresh'}
          </Button>
        </Col>
      </Row>

      <Nav variant="tabs" className="mb-4" activeKey={activeTab} onSelect={setActiveTab}>
        <Nav.Item>
          <Nav.Link eventKey="chains">Chain Status</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="runs">Update Runs</Nav.Link>
        </Nav.Item>
      </Nav>

      {activeTab === 'runs' && <RunHistory />}

      {activeTab === 'chains' && <>
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Summary cards */}
      <Row className="mb-4 g-3">
        {[
          { label: 'Total chains',    value: status ? status.length : '—',  bg: 'primary' },
          { label: 'Chains with data', value: chainsWithData,               bg: 'info' },
          { label: 'Total stores',    value: totalStores.toLocaleString(),   bg: 'success' },
          { label: 'Total scrape runs', value: totalRuns.toLocaleString(),   bg: 'secondary' },
          { label: 'Total errors',    value: totalErrors,                    bg: totalErrors > 0 ? 'danger' : 'success' },
          { label: 'Fresh chains',    value: `${freshChains} / ${chainsWithData}`, bg: 'warning' },
        ].map(card => (
          <Col key={card.label} xs={6} md={4} lg={2}>
            <Card text={card.bg === 'warning' ? 'dark' : 'white'} bg={card.bg} className="text-center h-100">
              <Card.Body className="py-2">
                <div style={{ fontSize: '1.6rem', fontWeight: 700, lineHeight: 1.1 }}>
                  {loading ? <Spinner size="sm" /> : card.value}
                </div>
                <div style={{ fontSize: '0.72rem' }}>{card.label}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Chain table */}
      <Card>
        <Card.Header><strong>Chain Status</strong></Card.Header>
        <Card.Body className="p-0">
          {loading && !status ? (
            <div className="p-4 text-center"><Spinner /></div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead className="table-light">
                <tr>
                  <SortHeader field="chainId" current={chainSort.field} dir={chainSort.dir} onSort={handleChainSort}>Chain</SortHeader>
                  <SortHeader field="storeCount" current={chainSort.field} dir={chainSort.dir} onSort={handleChainSort}>Stores</SortHeader>
                  <SortHeader field="totalRuns" current={chainSort.field} dir={chainSort.dir} onSort={handleChainSort}>Runs</SortHeader>
                  <SortHeader field="errorCount" current={chainSort.field} dir={chainSort.dir} onSort={handleChainSort}>Health</SortHeader>
                  <SortHeader field="lastAttempt" current={chainSort.field} dir={chainSort.dir} onSort={handleChainSort}>Last tried</SortHeader>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortRows(status || [], chainSort.field, chainSort.dir).map(chain => (
                  <React.Fragment key={chain.chainId}>
                    <ChainRow
                      chain={chain}
                      onExpand={handleExpand}
                      expanded={expandedChain === chain.chainId}
                    />
                    {expandedChain === chain.chainId && (
                      <ChainDetail chainId={chain.chainId} />
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      </>}
    </Container>
    </ChainSourcesContext.Provider>
  );
}
